import { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';

import { Button } from 'ui-components/components/button';
import { CheckboxSlider } from 'ui-components/components/checkbox-slider';
import { FlexColumn, FlexRow } from 'ui-components/components/flex';
import { Gap } from 'ui-components/components/flex/gap';
import { Input, version2 } from 'ui-components/components/input';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalFooterCancelButton } from 'ui-components/components/modal/modal-footer/cancel-button';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { Text } from 'ui-components/components/text';
import { TextType } from 'ui-components/components/text/text-type';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import style from './style.module.less';
import { ModalPortal } from '../../../../../../components/modal-portal';
import { useEnableInventoryLocationsBarcode } from '../../../../../../feature-flags/hooks/use-enable-inventory-locations-barcode';
import { createNewInventoryWarehouseLocation, updateInventoryWarehouseLocation } from '../../../../../../store/inventory/inventory.api';
import { updateSellerInventoryWarehousesLocations } from '../../../../../../store/seller/seller.actions';
import { baseColumns, baseColumnsWithBarcode, InventoryWarehouseLocationFactory } from '../../../constants';
import ErrorModal from '../../../modals/error-modal';
import messages from '../messages';

/**
 * Modal component for editing or creating an inventory warehouse location.
 * It allows users to input details such as area, shelf, unit, and bin.
 * @param {object} params
 * @param {object} [params.inventoryWarehouseLocation] - The inventory warehouse location object to edit, if any.
 * @param {string} params.inventoryWarehouseId - The ID of the inventory warehouse.
 * @param {() => void} params.onClose - Callback function to close the modal.
 * @returns {JSX.Element} The rendered modal component.
 */
const EditLocationModal = ({ inventoryWarehouseLocation, inventoryWarehouseId, onClose }) => {
  const dispatch = useDispatch();
  const isInventoryLocationsBarcodeEnabled = useEnableInventoryLocationsBarcode();
  const [isLoading, setIsLoading] = useAsyncSafeState(false);
  const [apiErrors, setApiErrors] = useAsyncSafeState();
  const [hasUserInput, setHasUserInput] = useAsyncSafeState(false);

  const availableInputFields = isInventoryLocationsBarcodeEnabled ? baseColumnsWithBarcode : baseColumns; // TODO: This will be continued and redesined with SPD-21683 and onwards
  const [formData, setFormData] = useState(() => {
    if (inventoryWarehouseLocation) {
      return { ...inventoryWarehouseLocation };
    }
    return new InventoryWarehouseLocationFactory(inventoryWarehouseId);
  });

  const isEditing = !!inventoryWarehouseLocation;
  const isValidated = formData.area || formData.shelf || formData.unit || formData.bin;

  const handleInputChange = useCallback(
    ({ target }) => {
      if (!hasUserInput) {
        setHasUserInput(true);
      }
      const { name, value } = target;
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    },
    [hasUserInput, setHasUserInput],
  );

  const handlePickableChange = useCallback(() => {
    setFormData((prev) => ({ ...prev, isPickable: !prev.isPickable }));
  }, []);

  const closeErrorModal = useCallback(() => setApiErrors(undefined), [setApiErrors]);

  const onSave = useCallback(() => {
    setIsLoading(true);

    // Transform empty strings to null for fields that should be nullable
    const sanitizedFormData = {
      ...formData,
      area: formData.area?.trim() === '' ? null : formData.area,
      unit: formData.unit?.trim() === '' ? null : formData.unit,
      shelf: formData.shelf?.trim() === '' ? null : formData.shelf,
      bin: formData.bin?.trim() === '' ? null : formData.bin,
      barcode: formData.barcode?.trim() === '' ? null : formData.barcode,
    };

    const apiCall = isEditing
      ? updateInventoryWarehouseLocation([sanitizedFormData])
      : createNewInventoryWarehouseLocation(sanitizedFormData);

    apiCall.promise
      .then(({ data, errors }) => {
        if (errors?.length > 0) {
          setApiErrors(errors);
        } else {
          dispatch(updateSellerInventoryWarehousesLocations(data));
          // onClose receives newLocation data to allow callers to immediately use the
          // newly created location (e.g., auto-selecting it in a dropdown)
          onClose(data);
        }
      })
      .catch((error) => {
        const errorArray = Array.isArray(error) ? error : [error];
        setApiErrors(errorArray);
      })
      .finally(() => setIsLoading(false));
  }, [formData, isEditing, dispatch, onClose, setApiErrors, setIsLoading]);

  return (
    <>
      <Modal size='medium'>
        <ModalHeader>
          {isEditing ? <FormattedMessage {...messages.editModalHeader} /> : <FormattedMessage {...messages.newLocationModalHeader} />}
        </ModalHeader>
        <ModalBody className={style['modal-body']}>
          <FlexRow className={style['input-fields']} gap={Gap.Space16}>
            {availableInputFields.map((field) => (
              <div key={field.id}>
                <label htmlFor={field.mappedKey}>
                  <FormattedMessage {...field.messageDescriptor} />
                </label>
                <Input
                  hasError={hasUserInput ? !isValidated : null}
                  id={field.mappedKey}
                  name={field.mappedKey}
                  onChange={handleInputChange}
                  value={formData[field.mappedKey] ?? ''}
                  version={version2}
                />
              </div>
            ))}
          </FlexRow>
          <FlexColumn className={style['additional-settings-section']} gap={Gap.Space12}>
            <Text textType={TextType.H4} value={messages.additionalSettingsSectionHeader} />
            <FlexRow className={style['pickable-row']}>
              <FlexColumn gap={Gap.Space4}>
                <Text textType={TextType.Body} value={messages.pickableLabel} />
                <Text className={style['pickable-description']} textType={TextType.Caption} value={messages.pickableDescription} />
              </FlexColumn>
              <CheckboxSlider checkboxId='pickable-slider' checked={formData.isPickable} onChange={handlePickableChange} />
            </FlexRow>
          </FlexColumn>
        </ModalBody>
        <ModalFooter>
          <ModalFooterCancelButton onClick={onClose} />
          <Button disabled={!isValidated || isLoading} onClick={onSave} theme={THEME_CONSTANTS.primary}>
            <FormattedMessage {...messages.manageLocationsModalGridSaveButton} />
          </Button>
        </ModalFooter>
      </Modal>

      {apiErrors ? (
        <ModalPortal onClose={closeErrorModal}>
          <ErrorModal errors={apiErrors} />
        </ModalPortal>
      ) : null}
    </>
  );
};

export { EditLocationModal };
